// /src/components/DisplayLineChart.jsx
import React, { useMemo,useState,useEffect } from "react";
import dayjs from "dayjs";
import HomeLineChart from "./charts/HomeLineChart";
import useMultiHealthApis from "./hooks/useMultiHealthApis";

import {
  ensurePct,
  clamp,
  toArray,
  mapStoreScore,
  parseDate,
  downsample,
  mapPercent,
  mapServicePercent,
  formatAlerts,
  pickUnhealthyCount,
} from "./charts/chartUtils";
import { useAccount } from './account-context';
import { getApiPaths } from "./constants/healthEndpoints";



const DAYS = 30;
// const BASE_URL = "https://xbzpe5g7i2ibwqyuwlw2uszjva0zhtqq.lambda-url.us-east-1.on.aws"; // optional override

const DEFAULT_COLORS = ["#5a6fa3", "#b5d56a"]; // [Store=blue, Service=green]
const Y_AXES = [
  { name: "Percent", unit: "%", color: "#b5d56a", position: "right", min: 0, max: 100 },
  { name: "Alert", unit: "Alerts", color: "#5a6fa3", position: "left", min: 0 },
];

/* ---------- helpers ---------- */

export default function DisplayLineChart() {
const { accountId } = useAccount();
  const API_URL_PATHS = getApiPaths(accountId);
const { MARKET_SERVICE_HEALTH, MARKET_STORE_HEALTH } = API_URL_PATHS;
const SERVICE_PATH = MARKET_SERVICE_HEALTH;; 
const STORE_PATH = MARKET_STORE_HEALTH;

  // const { dataByKey, loading, errorsByKey } = useMultiHealthApis({
  //   endpoints: {
  //     MarketServiceHealth: SERVICE_PATH,
  //     MarketStoreHealth: STORE_PATH,
  //   },
  //   // baseUrl: BASE_URL,
  //   retries: 1,
  //   timeout: 10000,
  // }, [accountId]);
const {
  dataByKey = {},
  loading = true,
  errorsByKey = {}
}

  = useMultiHealthApis({
    endpoints: {
      MarketServiceHealth: SERVICE_PATH,
      MarketStoreHealth: STORE_PATH,
    },
    // baseUrl: BASE_URL,
    retries: 1,
    timeout: 10000,
  }, [accountId])|| {};
  const { labels, series } = useMemo(() => {
    // 1) Build maps by ISO date (YYYY-MM-DD) for both datasets
    const storeMap = new Map();   // dateISO -> number
    const serviceMap = new Map(); // dateISO -> number

    toArray(dataByKey?.MarketStoreHealth).forEach((item) => {
      const d = parseDate(item);
      if (!d) return;
      const iso = d.format("YYYY-MM-DD");
      storeMap.set(iso, mapStoreScore(item));
    });

    toArray(dataByKey?.MarketServiceHealth).forEach((item) => {
      const d = parseDate(item);
      if (!d) return;
      const iso = d.format("YYYY-MM-DD");
      serviceMap.set(iso, mapServicePercent(item));
    });

    // 2) Build a sorted union of all dates we have from both series
    const allDates = Array.from(new Set([...storeMap.keys(), ...serviceMap.keys()])).sort();

    // 3) Clip to LAST N (DAYS) dates
    const usedDates = allDates.slice(-DAYS);

    // 4) Labels from date: "oct 1", "oct 2", ...
    const labels = usedDates.map((iso) => dayjs(iso).format("MMM D").toLowerCase());

    // 5) Series arrays aligned to labels (null for missing days)
    const storeData = usedDates.map((iso) =>
      storeMap.has(iso) ? storeMap.get(iso) : null
    );
    const serviceData = usedDates.map((iso) =>
      serviceMap.has(iso) ? serviceMap.get(iso) : null
    );

    // 6) Build the exact series shape expected by your HomeLineChart
    const series = [];

    if (storeData.some((v) => v !== null)) {
      series.push({
        name: "Store Health",
        data: storeData,
        yAxisIndex: 1, // left = Score
        smooth: false,
        area: false,
        lineStyle: { width: 2 },
        showSymbol: true,
        symbol: "circle",
        symbolSize: 6,
        emphasis: { focus: "series" },
      });
    }

    if (serviceData.some((v) => v !== null)) {
      series.push({
        name: "Service Health %",
        data: serviceData,
        yAxisIndex: 0, // right = Percent
        showSymbol: true,
        symbol: "circle",
        symbolSize: 6,
        emphasis: { focus: "series" },
        smooth: false,
        area: false,
        lineStyle: { width: 2 },
      });
    }

    return { labels, series };
  }, [dataByKey]);

   // Responsive chart height for better UI/UX
  const computeHeight = (w) => {
    if (!w) return 240;
    if (w >= 1400) return 480;
    if (w >= 1100) return 280;
    if (w >= 900) return 360;
    if (w >= 600) return 300;
    return 220; // small/mobile
  };
   const [chartHeight, setChartHeight] = useState(() =>
    typeof window !== "undefined" ? computeHeight(window.innerWidth) : 360
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setChartHeight(computeHeight(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
//console.log("Render DisplayLineChart with height:", chartHeight);
  /* ---------- Loading / Error UI ---------- */
  if (loading) {
    return (
      <section className="chart-card" aria-label="Platform health this month">
        <h3>Health by Day</h3>
        <div className="chart-container" role="region" aria-roledescription="line chart">
          <div>Loading health data…</div>
        </div>
      </section>
    );
  }

  if (Object.keys(errorsByKey || {}).length > 0) {
    return (
      <section className="chart-card" aria-label="Platform health this month">
        <h3>Health by Day</h3>
        <div className="chart-container" role="region" aria-roledescription="line chart">
          <div style={{ color: "red" }}>Failed to load health data</div>
        </div>
      </section>
    );
  }

  /* ---------- Render the chart ---------- */
  return (
    <section className="chart-card" aria-label="Platform health this month">
      <h3>Health by Day</h3>

      <div className="chart-container" role="region" aria-roledescription="line chart">
        <HomeLineChart
          title=""
          labels={labels}               
          series={series}               
          yAxes={[
            { name: "Percent", unit: "%", color: "#b5d56a", position: "right", min: 0, max: 100 },
            { name: "Alert", unit: "Alert", color: "#5a6fa3", position: "left"},
          ]}
          yMinBuffer={[35, 10]}
          colors={DEFAULT_COLORS}
          legendPosition="top"
          showLegend={true}
         height={240}  
          labelFormatter={(val) => `${val}`}
        />
      </div>
     
    </section>
  );
}
